import L from 'leaflet';
import type { Tone } from '../ui/Badge';
import { spreadFaces, type FacePlace, type FaceView } from './faceLayout';
import {
  DIM_OPACITY,
  FACE_SIZE,
  FACE_SIZE_PICKED,
  PIN_SIZE_SELECTED,
} from './markerStyle';

/**
 * A person on the map (the crew map's people layer): a face with their initials, ringed in
 * their STATE's tone.
 *
 * ⚠️ Never a measured position (ADR 0001): the page puts a face at the job they checked in
 * to, or halfway along the leg between two jobs while en route. So every face lies inside the
 * pins' own bounds, which is why faces never take part in a fit.
 */
export interface MapPerson {
  id: string;
  latitude: number;
  longitude: number;
  /** En route: the leg it is drawn on (dashed), and the only line it may slide along. */
  leg: { from: [number, number]; to: [number, number] } | null;
  initials: string;
  /** The ring: a tone of the app's one vocabulary (`badge--*` in theme.css), never per person. */
  tone: Tone;
  /** A line under the face ("left A at 10:42"). */
  tag: string | null;
  /** The face's accessible name. */
  label: string;
  dimmed?: boolean;
  picked?: boolean;
}

/**
 * The ONE dashed style: the van's order of the day and an en-route leg alike. Dashed because
 * neither is a road; never solid.
 */
export const DASHED: L.PolylineOptions = {
  color: '#e8590c',
  weight: 3,
  opacity: 0.75,
  dashArray: '6 6',
};

/**
 * Stacking, as a marker's `zIndexOffset` (Leaflet adds its screen y). Every face ABOVE the pins
 * (a selected pin is +1000), the picked face above everyone: a face under a pin is a tech
 * nobody can see. A face over a pin lets the pin's taps through instead (`passThrough`). The
 * tags go under everything: words are context, the pins are the facts.
 */
export const FACE_Z = 2000;
export const FACE_Z_PICKED = 3000;
export const TAG_Z = -2_000_000;

/**
 * The legs' own pane, ABOVE the markers (600) and below the popups (700): a short leg at a
 * wide zoom was hidden under its own transit face. Drawn thinner there, trimmed to the pins'
 * rims so it never crosses a job's number, and never interactive.
 */
export const LEG_PANE = 'crewLegs';
const LEG_PANE_Z = '650';
export const LEG_WEIGHT = 2;
/**
 * How far short of each pin's centre a leg stops: a SELECTED pin's radius and a pixel, so the
 * leg (drawn above the markers) never paints into an endpoint, selected or not, without a
 * relayout on every selection.
 */
const LEG_TRIM = PIN_SIZE_SELECTED / 2 + 1;

const escapeHtml = (s: string) =>
  s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );

/**
 * A face: initials on the surface colour, ringed in its tone (`badge--*` sets `--badge-hue`).
 *
 * ⚠️ The accessible name is a visually hidden span INSIDE the icon: Leaflet applies `alt` only
 * to an `<img>` icon. The marker is a `role="button"` (keyboard: true), so its name comes from
 * that span; the drawn initials are `aria-hidden`.
 */
function faceHtml(face: MapPerson): string {
  const size = face.picked ? FACE_SIZE_PICKED : FACE_SIZE;
  return `<span class="sr-only">${escapeHtml(face.label)}</span><span aria-hidden="true" class="badge--${face.tone}" style="
      display:flex;align-items:center;justify-content:center;
      width:${size}px;height:${size}px;border-radius:999px;box-sizing:border-box;
      background:var(--sw-surface);color:var(--sw-text);
      font:700 ${face.picked ? 13 : 12}px/1 system-ui,sans-serif;
      border:3px solid var(--badge-hue);
      box-shadow:${face.picked ? '0 0 0 3px var(--sw-surface), 0 0 0 6px var(--badge-hue), ' : ''}0 2px 6px rgb(0 0 0 / .35);
      opacity:${face.dimmed ? DIM_OPACITY : 1};
    ">${escapeHtml(face.initials)}</span>`;
}

/**
 * The words under a face, as a marker of their own drawn UNDER the pins: a long tag laid
 * across a pin must not hide it. Not interactive and `aria-hidden`: the face's name says it.
 */
function tagHtml(face: MapPerson): string {
  return `<span aria-hidden="true" style="
      position:absolute;left:0;top:0;transform:translateX(-50%);
      white-space:nowrap;background:var(--sw-surface);color:var(--sw-text);
      border:1px solid var(--sw-border);border-radius:999px;
      font:600 11px/1.4 system-ui,sans-serif;padding:1px 7px;
      box-shadow:0 1px 4px rgb(0 0 0 / .15);
      opacity:${face.dimmed ? DIM_OPACITY : 1};
    ">${escapeHtml(face.tag ?? '')}</span>`;
}

const faceIcon = (face: MapPerson, [dx, dy]: [number, number]) => {
  const size = face.picked ? FACE_SIZE_PICKED : FACE_SIZE;
  return L.divIcon({
    className: '',
    html: faceHtml(face),
    iconSize: [size, size],
    // The face's centre sits `offset` pixels from its point.
    iconAnchor: [size / 2 - dx, size / 2 - dy],
  });
};

const tagIcon = (face: MapPerson, [dx, dy]: [number, number]) => {
  const size = face.picked ? FACE_SIZE_PICKED : FACE_SIZE;
  return L.divIcon({
    className: '',
    html: tagHtml(face),
    iconSize: [0, 0],
    // Its top centre sits just under the face's bottom edge.
    iconAnchor: [0 - dx, 0 - (dy + size / 2 + 5)],
  });
};

/** What one person is drawn with, kept across draws so a marker is never rebuilt. */
interface Drawn {
  face: MapPerson;
  place: FacePlace;
  marker: L.Marker;
  /** What the marker shows now, so an unchanged face is not touched at all. */
  shown: string;
  tag: L.Marker | null;
  tagShown: string;
  leg: L.Polyline | null;
}

/**
 * A face lying over a job pin lets the pointer through to the pin, so the pin keeps its tap;
 * it stays drawn on top and focusable, and Enter or Space on it still picks its tech (as does
 * its chip). Set on the marker's own element, which `setIcon` refills but never replaces.
 */
function passThrough(marker: L.Marker, over: boolean): void {
  const el = marker.getElement();
  if (!el) return;
  // The whole face passes the pointer, not just the part over the pin: where they overlap the
  // pin's tap wins, and that tech stays pickable from its chip and by keyboard.
  el.style.pointerEvents = over ? 'none' : '';
  if (over) el.dataset.overPin = 'true';
  else delete el.dataset.overPin;
}

export interface PeopleLayer {
  /** Draw these people over these job pins: changed markers updated, never rebuilt. */
  draw(
    faces: readonly MapPerson[],
    pins: readonly { latitude: number; longitude: number }[],
    onPick: ((id: string) => void) | undefined,
  ): void;
  /** Lay the faces out again for the map as it is now (a pan, zoom, resize, new padding). */
  relayout(): void;
}

/**
 * The people layer: faces, their tags and legs, in a layer group of their own, so drawing
 * people never touches the job pins (an open pin popup stays open through a pick or a
 * refresh).
 *
 * ⭐ **Keyed by person.** A face's marker is made once and then moved, re-iconed and re-stacked
 * in place (`setLatLng`, `setIcon`, `setZIndexOffset`). Leaflet's `DivIcon` refills the SAME
 * element on `setIcon`, so the keyboard focus on a face survives a pick.
 *
 * ⚠️ **Enter and Space pick.** Leaflet 1.9 makes a `keyboard: true` marker focusable and a
 * `role="button"`, but never turns a key into a click: without the `keydown` handler a face is
 * reachable by Tab and does nothing.
 */
export function createPeopleLayer(map: L.Map, view: () => FaceView | null): PeopleLayer {
  const group = L.layerGroup().addTo(map);
  const pane = map.getPane(LEG_PANE) ?? map.createPane(LEG_PANE);
  pane.style.zIndex = LEG_PANE_Z;
  pane.style.pointerEvents = 'none';

  /** A leg's two ends on the map, each pulled back to its pin's rim; null when they touch. */
  const legEnds = (leg: NonNullable<MapPerson['leg']>): [L.LatLng, L.LatLng] | null => {
    const a = map.latLngToContainerPoint(leg.from);
    const b = map.latLngToContainerPoint(leg.to);
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len <= 2 * LEG_TRIM) return null;
    const ux = ((b.x - a.x) / len) * LEG_TRIM;
    const uy = ((b.y - a.y) / len) * LEG_TRIM;
    return [
      map.containerPointToLatLng([a.x + ux, a.y + uy]),
      map.containerPointToLatLng([b.x - ux, b.y - uy]),
    ];
  };
  const drawn = new Map<string, Drawn>();
  let pinPoints: readonly { latitude: number; longitude: number }[] = [];
  let pick: ((id: string) => void) | undefined;

  const layout = (faces: readonly MapPerson[]) =>
    spreadFaces(
      faces.map((f) => {
        const p = map.latLngToContainerPoint([f.latitude, f.longitude]);
        return {
          id: f.id,
          x: p.x,
          y: p.y,
          leg: f.leg
            ? {
                from: map.latLngToContainerPoint(f.leg.from),
                to: map.latLngToContainerPoint(f.leg.to),
              }
            : null,
          tag: f.tag,
        };
      }),
      pinPoints.map((p) => map.latLngToContainerPoint([p.latitude, p.longitude])),
      view(),
    );

  /** Bring one person's markers to `face` at `place`, touching only what changed. */
  const apply = (d: Drawn, face: MapPerson, place: FacePlace) => {
    d.face = face;
    d.place = place;
    const at = L.latLng(face.latitude, face.longitude);
    if (!d.marker.getLatLng().equals(at)) d.marker.setLatLng(at);
    const shown = `${faceHtml(face)}|${place.offset}`;
    if (shown !== d.shown) {
      d.shown = shown;
      d.marker.setIcon(faceIcon(face, place.offset));
    }
    d.marker.options.alt = face.label;
    d.marker.setZIndexOffset(face.picked ? FACE_Z_PICKED : FACE_Z);
    passThrough(d.marker, place.overPin);

    if (face.tag) {
      d.tag ??= L.marker(at, {
        icon: tagIcon(face, place.offset),
        interactive: false,
        keyboard: false,
        zIndexOffset: TAG_Z,
      }).addTo(group);
      if (!d.tag.getLatLng().equals(at)) d.tag.setLatLng(at);
      const tagShown = `${tagHtml(face)}|${place.offset}`;
      if (tagShown !== d.tagShown) {
        d.tagShown = tagShown;
        d.tag.setIcon(tagIcon(face, place.offset));
      }
      d.tag.setOpacity(place.showTag ? 1 : 0);
    } else if (d.tag) {
      group.removeLayer(d.tag);
      d.tag = null;
      d.tagShown = '';
    }

    if (face.leg) {
      const ends = legEnds(face.leg);
      const style = {
        ...DASHED,
        weight: LEG_WEIGHT,
        // Ends that touch (the two jobs side by side on screen): nothing to draw between.
        opacity:
          ends === null
            ? 0
            : face.dimmed
              ? DASHED.opacity! * DIM_OPACITY
              : DASHED.opacity,
      };
      const line = ends ?? [face.leg.from, face.leg.to];
      if (d.leg) {
        d.leg.setLatLngs(line);
        d.leg.setStyle(style);
      } else {
        d.leg = L.polyline(line, { ...style, pane: LEG_PANE, interactive: false }).addTo(
          group,
        );
      }
    } else if (d.leg) {
      group.removeLayer(d.leg);
      d.leg = null;
    }
  };

  const create = (face: MapPerson, place: FacePlace): Drawn => {
    const marker = L.marker([face.latitude, face.longitude], {
      icon: faceIcon(face, place.offset),
      keyboard: true,
      alt: face.label,
      zIndexOffset: FACE_Z,
      // A face's click picks its tech; it must never also reach the map as a background
      // click (which clears the selection). Leaflet's marker default, said explicitly.
      bubblingMouseEvents: false,
    });
    // The handlers read the CURRENT `pick`, so a new callback needs no new marker.
    marker.on('click', () => pick?.(face.id));
    marker.on('keydown', (e: L.LeafletKeyboardEvent) => {
      const key = e.originalEvent.key;
      if (key !== 'Enter' && key !== ' ') return;
      e.originalEvent.preventDefault();
      pick?.(face.id);
    });
    marker.addTo(group);
    const d: Drawn = {
      face,
      place,
      marker,
      shown: `${faceHtml(face)}|${place.offset}`,
      tag: null,
      tagShown: '',
      leg: null,
    };
    return d;
  };

  return {
    draw(faces, pins, onPick) {
      pinPoints = pins;
      pick = onPick;
      const ids = new Set(faces.map((f) => f.id));
      for (const [id, d] of drawn) {
        if (ids.has(id)) continue;
        group.removeLayer(d.marker);
        if (d.tag) group.removeLayer(d.tag);
        if (d.leg) group.removeLayer(d.leg);
        drawn.delete(id);
      }
      const places = layout(faces);
      for (const face of faces) {
        const place = places.get(face.id)!;
        const d = drawn.get(face.id) ?? create(face, place);
        drawn.set(face.id, d);
        apply(d, face, place);
      }
    },
    relayout() {
      const faces = [...drawn.values()].map((d) => d.face);
      const places = layout(faces);
      for (const d of drawn.values()) apply(d, d.face, places.get(d.face.id)!);
    },
  };
}
